import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatGBP } from "@/lib/format";
import { Countdown } from "@/components/countdown";
import { DataState, RowListSkeleton, EmptyState } from "@/components/data-state";
import { Button } from "@/components/ui/button";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { SellerBadgeStack } from "@/components/seller-badge-stack";
import { SellerHoverCard } from "@/components/seller-hover-card";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/my-bids")({
  head: () => ({ meta: [{ title: "My Bids" }] }),
  component: MyBidsPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function MyBidsPage() {
  const { t } = useTranslation("activity");
  const { user } = useAuth();
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["my-bids", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bids")
        .select("id,amount_pence,created_at,auction_id,auctions(id,title,seller_id,grading_company,grade,images,end_time,current_bid_pence,high_bidder_id,status,winner_id)")
        .eq("bidder_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const map = new Map<string, any>();
      for (const b of data ?? []) if (!map.has(b.auction_id)) map.set(b.auction_id, b);
      return Array.from(map.values());
    },
  });

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-primary/[0.06] via-transparent to-transparent" />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{t("myBids.eyebrow", "Buyer")}</p>
      <h1 className="mt-2 mb-8 font-display text-4xl sm:text-5xl">{t("myBids.title", "My Bids")}</h1>
      <DataState
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        retrying={isRefetching}
        data={data}
        isEmpty={(d) => d.length === 0}
        skeleton={<RowListSkeleton count={5} />}
        errorDescription={t("myBids.errorDescription", "We couldn't load your bids.")}
        empty={
          <EmptyState
            title={t("myBids.emptyTitle", "You haven't placed any bids yet.")}
            description={t("myBids.emptyDescription", "Find a card you love and place your first bid.")}
            action={
              <Button asChild className="bg-gold-gradient hover:opacity-90">
                <Link to="/auctions">{t("myBids.browseAuctions", "Browse auctions")}</Link>
              </Button>
            }
          />
        }
      >
        {(rows) => (
          <div className="space-y-3">
            {rows.map((b: any) => {
              const a = b.auctions;
              if (!a) return null;
              const winning = a.high_bidder_id === user?.id;
              const won = a.winner_id === user?.id;
              return (
                <Link
                  key={b.id}
                  to="/auction/$slug"
                  params={{ slug: buildAuctionSlug(a) }}
                  className="flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 hover:border-gold/60"
                >
                  <div className="w-16 h-20 rounded overflow-hidden bg-surface-2 shrink-0">
                    {a.images?.[0] && <img src={a.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{a.title}</p>
                    <p className="text-xs text-muted-foreground">{t("myBids.yourBid", "Your bid: {amount}", { amount: formatGBP(b.amount_pence) })}</p>
                    {a.seller_id && (
                      <div className="mt-1.5" onClick={(e) => e.preventDefault()}>
                        <SellerHoverCard sellerId={a.seller_id}>
                          <SellerBadgeStack sellerId={a.seller_id} compact size="sm" />
                        </SellerHoverCard>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{formatGBP(a.current_bid_pence)}</p>
                    <p className="text-xs">
                      {won ? <span className="text-gold">{t("myBids.won", "Won 🎉")}</span>
                       : a.status !== "active" ? <span className="text-muted-foreground">{t("myBids.ended", "Ended")}</span>
                       : winning ? <span className="text-gold">{t("myBids.winning", "Winning")}</span>
                       : <span className="text-destructive">{t("myBids.outbid", "Outbid")}</span>}
                    </p>
                    {a.status === "active" && <Countdown endTime={a.end_time} className="text-xs" />}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </DataState>
      </div>
    </div>
  );
}
